// CatalogComponentModal.js - Create Catalog Components (Redesigned)

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';
import { COMPONENT_LIBRARY, COMPONENT_CATEGORIES } from '../../data/componentLibrary';
import { createComponentNode } from '../../utils/componentNodeFactory';

const CatalogComponentModal = ({ isOpen, onClose, baseCategory = null, duplicateSource = null, editingComponent = null }) => {
  const { createCatalogComponent, updateCatalogComponent, catalogComponents, nodes, setNodes } = useStore();

  // Two-step state: 'details' or 'parameters'
  const [step, setStep] = useState('details');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Component data
  const [formData, setFormData] = useState({
    category: baseCategory || '',
    componentType: '',
    manufacturer: 'Guerrilla RF',
    partNumber: ''
  });

  const [parameters, setParameters] = useState({});

  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Reset on modal open/close
  useEffect(() => {
    if (isOpen) {
      // Check if we're in duplicate mode
      if (duplicateSource) {
        setStep('details');
        setFormData({
          category: baseCategory || '',
          componentType: duplicateSource.baseType,
          manufacturer: duplicateSource.manufacturer,
          partNumber: '' // User must provide new part number
        });
        setParameters(JSON.parse(JSON.stringify(duplicateSource.parameters)));
        setErrors({});
        setPosition({ x: 0, y: 0 });
      }
      // Check if we're in edit mode
      else if (editingComponent) {
        setStep('parameters'); // Skip to parameters step for editing
        setFormData({
          category: baseCategory || '',
          componentType: editingComponent.baseType,
          manufacturer: editingComponent.manufacturer,
          partNumber: editingComponent.partNumber
        });
        setParameters(JSON.parse(JSON.stringify(editingComponent.parameters)));
        setErrors({});
        setPosition({ x: 0, y: 0 });
      }
      // Normal create mode
      else {
        setStep('details');
        setFormData({
          category: baseCategory || '',
          componentType: '',
          manufacturer: 'Guerrilla RF',
          partNumber: ''
        });
        setParameters({});
        setErrors({});
        setPosition({ x: 0, y: 0 });
      }
    }
  }, [isOpen, baseCategory, duplicateSource, editingComponent]);

  // Dragging handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      let newX = position.x + deltaX;
      let newY = position.y + deltaY;
      const minVisible = 100;
      const maxX = window.innerWidth / 2 - minVisible;
      const minX = -window.innerWidth / 2 + minVisible;
      const maxY = window.innerHeight / 2 - minVisible;
      const minY = -window.innerHeight / 2 + minVisible;
      newX = Math.max(minX, Math.min(newX, maxX));
      newY = Math.max(minY, Math.min(newY, maxY));
      setPosition({ x: newX, y: newY });
      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, position]);

  if (!isOpen) return null;

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  // Get available component types for selected category
  const getComponentTypes = () => {
    if (!formData.category) return [];
    const category = COMPONENT_CATEGORIES[formData.category];
    if (!category) return [];
    return category.components.map(compType => ({
      value: compType,
      label: COMPONENT_LIBRARY[compType]?.displayName || compType
    }));
  };

  // Load default parameters when component type is selected
  const loadDefaultParameters = (componentType) => {
    // Check if any catalog components of this type exist
    const existingCatalogComps = catalogComponents.filter(
      comp => comp.type.startsWith(componentType) ||
              COMPONENT_LIBRARY[componentType]?.category === COMPONENT_LIBRARY[comp.type]?.category
    );

    let defaultParams;
    if (existingCatalogComps.length > 0) {
      // Use most recent catalog component's parameters
      const mostRecent = existingCatalogComps[existingCatalogComps.length - 1];
      defaultParams = mostRecent.parameters || {};
    } else {
      // Use generic component defaults
      defaultParams = COMPONENT_LIBRARY[componentType]?.parameters || {};
    }

    setParameters(JSON.parse(JSON.stringify(defaultParams)));
  };

  const validateDetailsForm = () => {
    const newErrors = {};
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.componentType) newErrors.componentType = 'Component type is required';
    if (!formData.manufacturer) newErrors.manufacturer = 'Manufacturer is required';
    if (!formData.partNumber?.trim()) newErrors.partNumber = 'Part number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateComponent = () => {
    if (!validateDetailsForm()) return;

    // Load default parameters and move to Step 2
    loadDefaultParameters(formData.componentType);
    setStep('parameters');
  };

  const handleParamChange = (paramKey, value) => {
    setParameters(prev => ({
      ...prev,
      [paramKey]: {
        ...prev[paramKey],
        value: parseFloat(value) || 0
      }
    }));
  };

  const handleSaveParameters = async () => {
    setIsSubmitting(true);
    try {
      // Ensure parameters are properly serializable
      const cleanParameters = JSON.parse(JSON.stringify(parameters));

      // Edit mode: update existing component
      if (editingComponent) {
        await updateCatalogComponent(editingComponent.id, {
          baseType: formData.componentType,
          displayName: formData.partNumber,
          category: formData.category,
          manufacturer: formData.manufacturer,
          partNumber: formData.partNumber,
          parameters: cleanParameters
        });

        // Update all nodes using this component type with new parameters
        const updatedNodes = nodes.map(node => {
          if (node.type === editingComponent.type) {
            return {
              ...node,
              data: {
                ...node.data,
                parameters: cleanParameters
              }
            };
          }
          return node;
        });
        setNodes(updatedNodes);

        onClose();
      }
      // Create mode: create new component
      else {
        const newComponent = await createCatalogComponent({
          baseType: formData.componentType,
          displayName: formData.partNumber,
          category: formData.category,
          manufacturer: formData.manufacturer,
          partNumber: formData.partNumber,
          parameters: cleanParameters
        });

        // Auto-add component instance to canvas
        const componentType = newComponent.type;
        const componentDef = COMPONENT_LIBRARY[formData.componentType];

        // Calculate position for new node
        let nodePosition;
        if (nodes.length === 0) {
          nodePosition = { x: 250, y: 150 };
        } else {
          const rightmostNode = nodes.reduce((rightmost, node) =>
            node.position.x > rightmost.position.x ? node : rightmost
          );
          nodePosition = { x: rightmostNode.position.x + 275, y: rightmostNode.position.y };
        }

        // Create component definition for factory (this is a newly created catalog component)
        const catalogComponentDef = {
          ...componentDef,
          type: componentType,
          isCatalog: true,
          partNumber: formData.partNumber,
          manufacturer: formData.manufacturer,
          baseType: formData.componentType,
          parameters: parameters,
          // These will be false initially since the component was just created
          hasFreqData: false,
          hasS2P: false
        };

        // Create new node using factory (ensures proper initialization)
        const newNode = createComponentNode(catalogComponentDef, {
          position: nodePosition,
          nodeType: componentType
        });

        if (!newNode) {
          throw new Error('Failed to create node for new catalog component');
        }

        setNodes([...nodes, newNode]);

        onClose();
      }
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to save component' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render Step 1: Details Form
  const renderDetailsForm = () => {
    const isComponentTypeReady = !!formData.category;
    const isSecondRowReady = !!formData.componentType;

    return (
      <FormContent>
        <FormRow>
          <FormGroup>
            <Label>Category *</Label>
            <Select
              value={formData.category}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, category: e.target.value, componentType: '' }));
                setErrors(prev => ({ ...prev, category: '' }));
              }}
            >
              <option value="">Select...</option>
              {Object.entries(COMPONENT_CATEGORIES).map(([key, cat]) => (
                <option key={key} value={key}>{cat.name}</option>
              ))}
            </Select>
            {errors.category && <ErrorText>{errors.category}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>Component Type *</Label>
            <Select
              $highlight={isComponentTypeReady && !formData.componentType}
              value={formData.componentType}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, componentType: e.target.value }));
                setErrors(prev => ({ ...prev, componentType: '' }));
              }}
              disabled={!formData.category}
            >
              <option value="">Select...</option>
              {getComponentTypes().map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </Select>
            {errors.componentType && <ErrorText>{errors.componentType}</ErrorText>}
          </FormGroup>
        </FormRow>

        <FormRow $disabled={!isSecondRowReady}>
          <FormGroup>
            <Label>Manufacturer *</Label>
            <Select
              value={formData.manufacturer}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, manufacturer: e.target.value }));
                setErrors(prev => ({ ...prev, manufacturer: '' }));
              }}
              disabled={!isSecondRowReady || !!editingComponent}
            >
              <option value="Guerrilla RF">Guerrilla RF</option>
              <option value="(other)">(other)</option>
            </Select>
            {errors.manufacturer && <ErrorText>{errors.manufacturer}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>Part Number *</Label>
            <Input
              $fullWidth
              type="text"
              value={formData.partNumber}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, partNumber: e.target.value }));
                setErrors(prev => ({ ...prev, partNumber: '' }));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateComponent();
                }
              }}
              placeholder="e.g., GRF0010"
              disabled={!isSecondRowReady || !!editingComponent}
              readOnly={!!editingComponent}
            />
            {errors.partNumber && <ErrorText>{errors.partNumber}</ErrorText>}
          </FormGroup>
        </FormRow>
      </FormContent>
    );
  };

  // Render Step 2: Parameters Editor
  const renderParametersForm = () => (
    <FormContent>
      <SubTitle>{formData.partNumber} ({formData.manufacturer})</SubTitle>

      <ParametersGrid>
        {Object.entries(parameters).map(([key, param]) => (
          <ParamGroup key={key}>
            <Label>
              {param.name}{param.unit && ` (${param.unit})`}
            </Label>
            <Input
              type="number"
              value={param.value}
              onChange={(e) => handleParamChange(key, e.target.value)}
              min={param.min}
              max={param.max}
              step={param.min !== undefined && param.max !== undefined
                ? (param.max - param.min) / 100
                : 0.1
              }
            />
          </ParamGroup>
        ))}
      </ParametersGrid>

      {errors.submit && <ErrorText>{errors.submit}</ErrorText>}
    </FormContent>
  );

  return (
    <Overlay onClick={onClose}>
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        }}
        $isDragging={isDragging}
      >
        <ModalHeader onMouseDown={handleMouseDown}>
          <ModalTitle>
            {editingComponent
              ? 'Edit Catalog Component'
              : step === 'details'
                ? 'Create Catalog Component'
                : 'Edit Parameters'}
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {step === 'details' ? renderDetailsForm() : renderParametersForm()}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            {step === 'parameters' && (
              <Button variant="secondary" onClick={() => setStep('details')}>
                Back
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            {step === 'details' ? (
              <Button variant="primary" onClick={handleCreateComponent}>
                Next: Edit Parameters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSaveParameters} disabled={isSubmitting}>
                {isSubmitting
                  ? (editingComponent ? 'Saving...' : 'Creating...')
                  : (editingComponent ? 'Save Changes' : 'Save & Add to Canvas')}
              </Button>
            )}
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${THEME.zIndex.modal};
  animation: fadeIn 150ms ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  background: ${THEME.panelBackground};
  border: 1px solid #404040;
  border-radius: ${THEME.borderRadiusLarge};
  box-shadow: ${THEME.shadowLarge};
  width: 360px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 250ms ease;
  transition: ${props => props.$isDragging ? 'none' : 'transform 0.1s ease-out'};
  user-select: ${props => props.$isDragging ? 'none' : 'auto'};

  @keyframes slideUp {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${THEME.borderColor};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const ModalTitle = styled.h2`
  color: ${THEME.textPrimary};
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 150ms ease;

  &:hover {
    background: ${THEME.hoverBackground};
    color: ${THEME.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  opacity: ${props => props.$disabled ? 0.4 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  transition: opacity 200ms ease;
`;

const FormTitle = styled.h3`
  color: ${THEME.textPrimary};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const SubTitle = styled.p`
  color: ${THEME.textSecondary};
  font-size: 14px;
  margin: 0 0 8px 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Label = styled.label`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.sm};
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`;

const Select = styled.select`
  width: 100%;
  background: ${THEME.backgroundDark};
  border: 2px solid ${props => props.$highlight ? THEME.accentBlue : THEME.borderColor};
  border-radius: 6px;
  padding: 8px 10px;
  color: ${THEME.textPrimary};
  font-size: 13px;
  outline: none;
  cursor: pointer;
  text-align: center;
  text-align-last: center;
  box-shadow: ${props => props.$highlight ? `0 0 8px ${THEME.accentBlue}40` : 'none'};
  transition: border-color 200ms ease, box-shadow 200ms ease;

  &:focus {
    border-color: ${THEME.accentBlue};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-width: 1px;
    box-shadow: none;
  }

  option {
    background: ${THEME.backgroundDark};
    color: ${THEME.textPrimary};
    text-align: center;
  }
`;

const Input = styled.input`
  width: ${props => props.$fullWidth ? '100%' : '120px'};
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  transition: border-color ${THEME.transitionFast};
  text-align: center;

  &:focus {
    border-color: ${THEME.accentPrimary};
    outline: none;
  }

  &::placeholder {
    color: ${THEME.textTertiary};
  }

  /* Remove spinner arrows for number inputs */
  &[type="number"]::-webkit-inner-spin-button,
  &[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const HelpText = styled.span`
  color: ${THEME.textTertiary};
  font-size: 12px;
  text-align: center;
`;

const ErrorText = styled.span`
  color: ${THEME.errorColor || '#ef4444'};
  font-size: 12px;
  margin-top: 4px;
  text-align: center;
`;

const ParametersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
`;

const ParamGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${THEME.spacing.sm};
  margin-bottom: ${THEME.spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid ${THEME.borderColor};
  gap: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: ${THEME.accentBlue};
    color: white;

    &:hover:not(:disabled) {
      background: ${THEME.accentBlueHover || '#3b82f6'};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : `
    background: ${THEME.backgroundLight};
    color: ${THEME.textPrimary};
    border: 1px solid ${THEME.borderColor};

    &:hover {
      background: ${THEME.hoverBackground};
    }
  `}
`;

export default CatalogComponentModal;
